/**
 * @name: QV Quantity Select
 * @description: ADA quantity combobox — hidden native select for form, button combobox + listbox for keyboard/AT
 * @dependencies: minicart-sidecart.css
 */

class QuantitySelect extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.init();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.onOutsideClick) {
      document.removeEventListener('click', this.onOutsideClick, true);
    }
    if (this.onEscapeKeydown) {
      document.removeEventListener('keydown', this.onEscapeKeydown, true);
    }
  }

  init() {
    this.select = this.querySelector('select[data-quantity-select]');
    this.listbox = this.querySelector('[data-quantity-listbox]');
    if (!this.select || !this.listbox) return;

    this.committed = 0;
    this.active = 0;
    this.isOpen = false;
    this.onOutsideClick = null;

    this.setupCombobox();

    this.combobox.addEventListener('mousedown', (event) => {
      event.preventDefault();
      if (document.activeElement !== this.combobox) this.combobox.focus();
      this.isOpen ? this.close(false) : this.open();
    });
    this.combobox.addEventListener('keydown', (event) => this.onKeydown(event));

    this.listbox.addEventListener('click', (event) => {
      const option = event.target.closest('[role="option"]');
      if (!option) return;
      this.active = Number(option.dataset.index) || 0;
      this.close(true);
    });

    this.observer = new MutationObserver(() => this.syncOptions());
    this.observer.observe(this.select, { childList: true, subtree: true });

    this.syncOptions();
  }

  setupCombobox() {
    this.combobox = this.querySelector('[data-quantity-combobox]');

    if (!this.combobox) {
      this.combobox = document.createElement('button');
      this.combobox.type = 'button';
      this.combobox.className = this.select.className;
      this.combobox.classList.remove('quantity__select');
      this.combobox.dataset.quantityCombobox = '';
      this.select.insertAdjacentElement('beforebegin', this.combobox);
    }

    this.hideNativeSelect();

    this.combobox.setAttribute('role', 'combobox');
    this.combobox.setAttribute('aria-haspopup', 'listbox');
    this.combobox.setAttribute('aria-controls', this.listbox.id);
    this.combobox.setAttribute('aria-expanded', 'false');
    this.combobox.setAttribute('aria-autocomplete', 'none');

    const label =
      this.querySelector(`label[for="${this.select.id}"]`) ||
      document.querySelector(`label[for="${this.select.id}"]`);

    if (label) {
      if (!label.id) label.id = `qty-label-${this.select.id}`;
      this.combobox.setAttribute('aria-labelledby', label.id);
      this.listbox.setAttribute('aria-labelledby', label.id);
    } else if (this.select.getAttribute('aria-label')) {
      this.combobox.setAttribute('aria-label', this.select.getAttribute('aria-label'));
    } else {
      this.combobox.setAttribute('aria-label', 'Select quantity');
    }
  }

  hideNativeSelect() {
    this.select.classList.add('quantity-select__native');
    this.select.setAttribute('aria-hidden', 'true');
    this.select.tabIndex = -1;
    this.select.removeAttribute('aria-haspopup');
    this.select.removeAttribute('aria-controls');
    this.select.removeAttribute('aria-expanded');
    this.select.removeAttribute('aria-activedescendant');
  }

  getOptions() {
    return Array.from(this.select.options).filter((option) => !option.disabled);
  }

  positionListbox() {
    const openUp = this.getBoundingClientRect().bottom + this.listbox.offsetHeight > window.innerHeight;
    this.listbox.classList.toggle('quantity-select__listbox--up', openUp);
    this.listbox.classList.toggle('quantity-select__listbox--down', !openUp);
  }

  render() {
    const options = this.getOptions();
    const listboxOptions = this.listbox.querySelectorAll('[role="option"]');

    for (const [index, element] of listboxOptions.entries()) {
      const text = options[index]?.textContent.trim() || element.textContent.trim();
      const isCommitted = index === this.committed;

      element.setAttribute('aria-selected', isCommitted ? 'true' : 'false');
      element.setAttribute('aria-label', isCommitted ? `${text} selected` : text);
      element.classList.toggle(
        'quantity-select__option--active',
        this.isOpen && index === this.active
      );
    }

    const activeListboxOption = listboxOptions[this.active];

    if (this.isOpen && activeListboxOption) {
      this.combobox.setAttribute('aria-activedescendant', activeListboxOption.id);
    } else {
      this.combobox.removeAttribute('aria-activedescendant');
    }

    const committedOption = options[this.committed];

    if (committedOption) {
      this.combobox.textContent = committedOption.textContent.trim();
    }
  }

  close(commit) {
    if (!this.isOpen) return;

    if (this.onOutsideClick) {
      document.removeEventListener('click', this.onOutsideClick, true);
      this.onOutsideClick = null;
    }

    if (this.onEscapeKeydown) {
      document.removeEventListener('keydown', this.onEscapeKeydown, true);
      this.onEscapeKeydown = null;
    }

    if (commit) {
      const selected = this.getOptions()[this.active];

      if (selected) {
        this.select.value = selected.value;
        this.committed = this.active;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      this.active = this.committed;
    }

    this.isOpen = false;
    this.combobox.setAttribute('aria-expanded', 'false');
    this.listbox.hidden = true;
    this.render();
    this.combobox.focus();
  }

  open() {
    if (this.isOpen || this.getOptions().length <= 1) return;

    this.isOpen = true;
    this.active = this.committed;
    this.combobox.setAttribute('aria-expanded', 'true');
    this.listbox.hidden = false;
    this.render();
    this.positionListbox();

    this.onOutsideClick = (event) => {
      if (!this.contains(event.target)) this.close(false);
    };
    document.addEventListener('click', this.onOutsideClick, true);

    this.onEscapeKeydown = (event) => {
      if (event.key !== 'Escape' || !this.isOpen) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.close(false);
    };
    document.addEventListener('keydown', this.onEscapeKeydown, true);
  }

  move(direction) {
    const next = this.active + direction;

    if (next >= 0 && next < this.getOptions().length) {
      this.active = next;
      this.render();
    }
  }

  onKeydown(event) {
    if (this.getOptions().length <= 1) return;

    const key = event.key;

    if (!this.isOpen) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === ' ' || key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.open();

        if (key === 'ArrowDown') this.move(1);
        if (key === 'ArrowUp') this.move(-1);
      }

      return;
    }

    if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close(false);
      return;
    }

    if (key === 'Tab') {
      this.close(false);
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      this.move(key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.close(true);
    }
  }

  syncOptions() {
    if (this.isOpen) this.close(false);

    const options = this.getOptions();

    this.listbox.innerHTML = options
      .map(
        (option, index) =>
          `<li role="option" id="qty-opt-${this.select.id}-${option.value}" data-index="${index}">${option.textContent.trim()}</li>`
      )
      .join('');

    const index = Math.max(0, options.findIndex((option) => option.value === this.select.value));
    this.committed = index;
    this.active = index;
    this.render();
  }
}
customElements.define('quantity-select', QuantitySelect);
